import { Popover, Text } from '@medly-components/core';
import { AxisTickProps } from '@nivo/axes';
import { ScatterPlotDatum, ScatterPlotSvgProps } from '@nivo/scatterplot';
import { format } from 'date-fns';

import { defaultTheme } from 'theme';

const CustomBottomTick = (tick: AxisTickProps<string>) => (
    <g transform={`translate(${tick.x},${tick.y + 32})`}>
        <text textAnchor="middle" dominantBaseline="middle">
            {format(new Date(tick.value), 'dd')}
        </text>
        <text textAnchor="middle" dominantBaseline="middle" y={20}>
            {format(new Date(tick.value), 'MMM')}
        </text>
    </g>
);

const CustomLeftTick = (tick: AxisTickProps<string>) => {
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const formatTickValue = (value: string, limit: number) => {
        if (value.length <= limit) return [value];
        const firstLine = value.substring(0, limit);
        const secondLine = value.length > 2 * limit ? value.substring(limit, 2 * limit - 1) + '...' : value.substring(limit);
        return [firstLine, secondLine];
    };
    const xTranslate = tick.x - 90;
    const yTranslate = tick.y;

    const lines = formatTickValue(tick?.value || '', 12);

    if (tick?.value === 'UP_ARROW' || tick?.value === 'DOWN_ARROW') {
        return <></>;
    }

    return (
        <g transform={`translate(${xTranslate},${yTranslate})`} style={{ opacity: 1 }}>
            <foreignObject
                width={90}
                height={100}
                transform={isSafari ? `translate(${xTranslate}px,${yTranslate}px)` : ''}
                style={{
                    flexDirection: 'column',
                    display: 'flex',
                    overflow: 'visible',
                    cursor: 'pointer'
                }}
            >
                <Popover style={{ position: 'unset' }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <Text textVariant="body3" textWeight="Medium">
                            {lines[0]}
                        </Text>
                        {lines[1] && (
                            <Text textVariant="body3" textWeight="Medium">
                                {lines[1]}
                            </Text>
                        )}
                    </div>
                    {lines[1] && (
                        <Popover.Popup
                            placement="right-start"
                            withArrow
                            bgColor="white"
                            style={{
                                transform: isSafari ? `translate(${tick.x + 10}px,${tick.y + 30}px)` : 'translate(0,-10px)'
                            }}
                        >
                            <Text textVariant="body3" textWeight="Medium" style={{ width: 'max-content' }}>
                                {tick.value}
                            </Text>
                        </Popover.Popup>
                    )}
                </Popover>
            </foreignObject>
        </g>
    );
};

export const getScatterPlotCommonProp = <RawDatum extends ScatterPlotDatum>(): Partial<ScatterPlotSvgProps<RawDatum>> => ({
    nodeSize: 16,
    animate: false,
    blendMode: 'multiply',
    colors: { scheme: 'category10' },
    margin: { top: 40, right: 40, bottom: 60, left: 100 },
    xScale: {
        type: 'time',
        format: '%Y-%m-%d',
        precision: 'day',
        useUTC: false
    },
    xFormat: 'time:%Y-%m-%d',
    axisBottom: {
        format: '%b %d',
        tickValues: 'every 1 days',
        ticksPosition: 'after',
        tickPadding: 10,
        renderTick: CustomBottomTick
    },
    yScale: {
        type: 'point'
    },
    axisLeft: {
        tickPadding: 12,
        renderTick: CustomLeftTick
    },
    useMesh: false,
    layers: ['grid', 'nodes', 'markers', 'mesh', 'legends', 'annotations', 'axes'],
    theme: {
        axis: {
            ticks: {
                line: {
                    opacity: 0
                }
            },
            domain: {
                line: {
                    stroke: defaultTheme.colors.grey[300],
                    strokeWidth: 1
                }
            }
        },
        grid: {
            line: {
                stroke: defaultTheme.colors.grey[300],
                strokeDasharray: '4 6'
            }
        },
        tooltip: {
            basic: {
                width: 'fit-content'
            }
        }
    }
});
